/**
 * P13 (owner's note 22:50): "Wstecz / Dalej" between the screens of the app, as in a browser. The steps are the
 * browser's own history (react-router keeps the entry's number in `history.state.idx`); this file only remembers
 * which app screen each entry of the tab is, so the buttons know whether a step stays inside the app and the list
 * of recent places can name them. Pure helpers; `navStore` keeps the list in the tab's session storage.
 */

export type Place = { idx: number; path: string; title: string };
export type NavAction = 'PUSH' | 'REPLACE' | 'POP' | 'LOAD';

/** Places kept on each side of the current one (the list shows at most `RECENT`). */
const KEEP = 50;
export const RECENT = 15;

const outside = (path: string) => path === '/login' || path.startsWith('/login?') || path.startsWith('/odpowiedz/');

export function recordPlace(stack: Place[], at: { idx: number; path: string; action: NavAction; title: string }): Place[] {
  const known = stack.find((p) => p.idx === at.idx);
  let next: Place[];
  if (at.action === 'PUSH') next = stack.filter((p) => p.idx < at.idx);
  else if (at.action === 'LOAD' && known?.path !== at.path) next = stack.filter((p) => p.idx < at.idx); // a new page load at this number
  else next = stack.filter((p) => p.idx !== at.idx);
  const keepTitle = (at.action === 'POP' || at.action === 'LOAD') && known?.path === at.path;
  next.push({ idx: at.idx, path: at.path, title: keepTitle ? known.title : at.title });
  return next.filter((p) => Math.abs(p.idx - at.idx) <= KEEP).sort((a, b) => a.idx - b.idx);
}

export function canGoBack(stack: Place[], idx: number): boolean {
  if (idx <= 0) return false;
  const prev = stack.find((p) => p.idx === idx - 1);
  // an entry not remembered (storage blocked) is still this tab's app history: the first page has idx 0
  return !prev || !outside(prev.path);
}

export function canGoForward(stack: Place[], idx: number): boolean {
  const next = stack.find((p) => p.idx === idx + 1);
  return !!next && !outside(next.path);
}

export type RecentPlace = Place & { delta: number };

/** The places behind (nearest first) and ahead of the current one, each with the step that leads there. */
export function recentPlaces(stack: Place[], idx: number): { back: RecentPlace[]; forward: RecentPlace[] } {
  const walk = (dir: -1 | 1) => {
    const out: RecentPlace[] = [];
    for (let d = dir; out.length < RECENT; d += dir) {
      const place = stack.find((p) => p.idx === idx + d);
      if (!place || outside(place.path)) break;
      out.push({ ...place, delta: d });
    }
    return out;
  };
  return { back: walk(-1), forward: walk(1) };
}

/** The screen's name by its address; screens with a more precise name (an event, a category) set it themselves. */
const TITLES: [string, string][] = [
  ['/finanse/cenniki/schematy-dni', 'Cenniki — Schematy dni'],
  ['/finanse/cenniki/uslugi', 'Cenniki — Usługi'],
  ['/finanse/cenniki/transport', 'Cenniki — Transport'],
  ['/finanse/cenniki/pakiety', 'Cenniki — Pakiety'],
  ['/finanse/cenniki/bloki-kosztorysow', 'Cenniki — Bloki kosztorysów'],
  ['/katalog/dostawcy', 'Katalogi dostawców'],
  ['/finanse/cenniki/ustawienia-kosztorysu', 'Cenniki — Ustawienia kosztorysu'],
  ['/finanse/cenniki', 'Cenniki'],
  ['/finanse/podsumowanie', 'Finanse — Podsumowanie'],
  ['/finanse/koszty-stale', 'Finanse — Koszty stałe'],
  ['/finanse/ustawienia', 'Finanse — Ustawienia'],
  ['/finanse/wyciagi', 'Finanse — Wyciągi'],
  ['/finanse/umowy/szablony', 'Finanse — Szablony umów'],
  ['/finanse/umowy', 'Finanse — Umowy'],
  ['/finanse/barter', 'Finanse — Barter'],
  ['/finanse/zakupy', 'Finanse — Zakupy'],
  ['/finanse/dostawcy', 'Finanse — Dostawcy'],
  ['/finanse/urzadzenia', 'Finanse — Urządzenia'],
  ['/finanse', 'Finanse'],
  ['/do-zrobienia', 'Do zrobienia'],
  ['/podsumowanie', 'Podsumowanie'],
  ['/katalog/kolejnosc-kategorii', 'Katalog — Kolejność kategorii'],
  ['/katalog', 'Katalog sprzętu'],
  ['/szablony/zestawy', 'Szablony — Zestawy'],
  ['/szablony/bloki', 'Szablony — Bloki'],
  ['/szablony', 'Szablony'],
  ['/reguly', 'Reguły'],
  ['/wyceny', 'Wyceny'],
  ['/dostepnosc', 'Dostępność'],
  ['/nowa-impreza', 'Nowe wydarzenie'],
  ['/nowe-wypozyczenie', 'Nowe wypożyczenie'],
  ['/moj-kalendarz', 'Mój kalendarz'],
  ['/zadania', 'Zadania'],
  ['/osoby/funkcje', 'Funkcje'],
  ['/osoby', 'Ludzie'],
  ['/kontrahenci', 'Kontrahenci'],
  ['/uzytkownicy', 'Użytkownicy'],
  ['/uprawnienia', 'Uprawnienia'],
  ['/ustawienia', 'Ustawienia firmy'],
  ['/powiadomienia/wysylka', 'Wysyłka powiadomień'],
  ['/powiadomienia', 'Powiadomienia'],
  ['/tresc-podpowiedzi', 'Treść podpowiedzi'],
  ['/instrukcja', 'Instrukcja'],
  ['/haslo', 'Zmiana hasła'],
];

export function titleFor(pathname: string): string {
  if (pathname === '/') return 'Strona główna';
  if (/^\/wydarzenia\/[^/]+\/sprzetowka$/.test(pathname)) return 'Sprzętówka';
  if (/^\/wydarzenia\/[^/]+\/zaladunek$/.test(pathname)) return 'Załadunek';
  if (/^\/wydarzenia\/[^/]+\/zwrot$/.test(pathname)) return 'Zwrot';
  if (pathname.startsWith('/wydarzenia')) return 'Wydarzenia';
  if (/^\/uzytkownicy\/[^/]+\/uprawnienia$/.test(pathname)) return 'Uprawnienia użytkownika';
  const hit = TITLES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  return hit ? hit[1] : 'Mek Records';
}

type Keys = { key: string; code: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean };

/**
 * The step of a key press: ⌘[ / ⌘] as in Safari (Ctrl on other systems) — also in text fields, where they edit
 * nothing; Alt+← / Alt+→ only outside text fields (inside one they move the cursor by a word). Else null.
 */
export function backKeyOf(e: Keys, mac: boolean, inTextField: boolean): -1 | 1 | null {
  if (e.shiftKey) return null;
  const command = mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (command && !e.altKey) {
    if (e.key === '[' || e.code === 'BracketLeft') return -1;
    if (e.key === ']' || e.code === 'BracketRight') return 1;
    return null;
  }
  if (e.altKey && !e.metaKey && !e.ctrlKey && !inTextField) {
    if (e.key === 'ArrowLeft') return -1;
    if (e.key === 'ArrowRight') return 1;
  }
  return null;
}
